import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Skeleton } from "../components/ui/skeleton";
import { ActionItem, ActionItemStatus } from "../types/actionItems";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { actionItemsService } from "../services/supabase/action-items";
import { ActionItemHeader } from "../components/action-item/detail/ActionItemHeader";
import { ActionItemMetadata } from "../components/action-item/detail/ActionItemMetadata";
import { ActionItemDescription } from "../components/action-item/detail/ActionItemDescription";
import { ActionItemObjective } from "../components/action-item/detail/ActionItemObjective";
import { ActionItemStepsResources } from "../components/action-item/detail/ActionItemStepsResources";
import { ActionItemContext } from "../components/action-item/ActionItemContext";
import { ActionItemNotes } from "../components/action-item/ActionItemNotes";
import { useState } from "react";
import { meetingNotesService } from "../services/supabase/meeting-notes";
import React from "react";

export function ActionItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showContext, setShowContext] = useState(false);
  const [notesText, setNotesText] = useState("");

  const { data: actionItem, isLoading, error } = useQuery({
    queryKey: ["actionItem", id, user?.id],
    queryFn: async () => {
      if (!id || !user?.id) throw new Error("Invalid action item ID or user not authenticated");
      return actionItemsService.getActionItem(id, user.id);
    },
    enabled: !!id && !!user?.id,
    meta: {
      onSuccess: (data: ActionItem & { meetings?: { title: string } | null }) => {
        if (data?.additional_notes) {
          setNotesText(data.additional_notes);
        }
      }
    }
  });

  const { data: meetingNotes = null } = useQuery({
    queryKey: ["meetingNotes", actionItem?.meeting_id],
    queryFn: async () => {
      if (!actionItem?.meeting_id || !user?.id) return null;
      return meetingNotesService.getMeetingNotes(actionItem.meeting_id, user.id);
    },
    enabled: !!actionItem?.meeting_id && !!user?.id,
  });

  // Parse steps and resources from JSON strings if they exist
  const parsedSteps = actionItem?.steps ? 
    (typeof actionItem.steps === 'string' ? JSON.parse(actionItem.steps) : actionItem.steps) : 
    [];

  const parsedResources = actionItem?.resources_needed ? 
    (typeof actionItem.resources_needed === 'string' ? JSON.parse(actionItem.resources_needed) : actionItem.resources_needed) : 
    [];

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<ActionItem>) => {
      if (!id || !user?.id) throw new Error("Invalid action item ID or user not authenticated");
      return actionItemsService.updateActionItem(id, user.id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["actionItem", id] });
      toast({
        title: "Success",
        description: "Changes saved successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating action item:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id || !user?.id) throw new Error("Invalid action item ID or user not authenticated");
      return actionItemsService.deleteActionItem(id, user.id);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Action item deleted successfully",
      });
      navigate("/app/action-items");
    },
    onError: (error) => {
      console.error("Error deleting action item:", error);
      toast({
        title: "Error",
        description: "Failed to delete action item",
        variant: "destructive",
      });
    },
  });

  const handleUpdate = async (updates: Partial<ActionItem>) => {
    await updateMutation.mutateAsync(updates);
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this action item?")) {
      deleteMutation.mutate();
    }
  };

  const handleStatusChange = (status: ActionItemStatus) => {
    handleUpdate({ status });
  };

  const handleDueDateChange = (due_date: string) => {
    handleUpdate({ due_date });
  };

  const handleSaveNotes = async () => {
    handleUpdate({ additional_notes: notesText });
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (error || !actionItem) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-foreground">Error Loading Action Item</h1>
        <p className="text-muted-foreground">
          {error?.message || "Action item not found"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-background p-6 space-y-4">
        <ActionItemHeader
          title={actionItem.title}
          onDelete={handleDelete}
          onTitleChange={(title) => handleUpdate({ title })}
        />

        <ActionItemMetadata
          item={actionItem}
          onStatusChange={handleStatusChange}
          onDueDateChange={handleDueDateChange}
        />
      </div>
      <div className="border-b border-border mx-4" />

      <div className="bg-background p-6 space-y-6 rounded-lg">
        <ActionItemObjective
          objective={actionItem.objective || ""}
          onChange={(objective) => handleUpdate({ objective })}
        />

        <ActionItemDescription
          description={actionItem.description || ""}
          onChange={(description) => handleUpdate({ description })}
        />

        <ActionItemStepsResources
          steps={parsedSteps}
          resources={parsedResources}
        />
      </div>

      {actionItem.meeting_id && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ActionItemContext
            actionItem={actionItem}
            showContext={showContext}
            setShowContext={setShowContext}
            meetingNotes={meetingNotes}
          />
          <ActionItemNotes
            notesText={notesText}
            setNotesText={setNotesText}
            handleSaveNotes={handleSaveNotes}
            meetingNotes={meetingNotes}
          />
        </div>
      )}
    </div>
  );
}